// Data layer — typed Firestore reads + callable wrappers.
// Screens use these instead of touching the SDK directly.

import {
  collection, doc, onSnapshot, query, orderBy, getDocs,
  type Unsubscribe,
} from "firebase/firestore";
import { db, callable } from "./firebase";

// ---- shared types ----
export interface Guest {
  code?: string;
  displayName: string;
  realName?: string;
  role: "guest" | "admin";
  intelPoints: number;
  totalIntel: number;
  avatarStatus: "none" | "pending" | "ready" | "failed";
  avatarPath: string | null;
  pushEnabled?: boolean;
  shareLocation?: boolean;
}
export interface Progress { status: "locked" | "unlocked" | "solved"; attempts?: number; }
export interface CaseFile {
  id: string; title: string; riddlePrompt: string;
  // Optional: a case may profile a specific guest (they can't crack their own),
  // or be a standalone question. May carry an image.
  subjectGuestId?: string | null;
  imageUrl?: string | null;
  releaseAt?: { toMillis(): number };
  released?: boolean; // admin manual immediate release
}
export interface Cache {
  id: string; name: string; powerTier: number; riddlePrompt: string;
  // "gun" = water-gun ordnance (default), "powerup" = supply drop (snacks/beers).
  kind?: "gun" | "powerup";
  locationText: string; hintCost: number; revealCost?: number;
  firstClaimedBy: string | null;
  finders?: string[];
  claimPhotos?: Record<string, string>;
  lat?: number; lng?: number; // published once first-claimed
  // Public rough search zone shown on the map before the exact pin is earned.
  roughLat?: number; roughLng?: number; roughRadius?: number;
  // Photo of the hiding spot, shown once location is unlocked to help agents find it.
  locationPhotoUrl?: string;
  releaseAt?: { toMillis(): number };
}
export interface CacheProgress {
  status?: "claimed";
  locationRevealed?: boolean;
  revealedVia?: "riddle" | "intel";
  lat?: number; lng?: number;
  proofPath?: string;
  hintsRevealed?: number;
  hintTexts?: string[];
}
export interface Broadcast {
  id: string; title: string; body: string; type: string; audience: string;
  createdAt?: { toMillis(): number };
}
export interface AgentPosition {
  id: string; lat: number; lng: number; accuracy?: number;
  codename?: string; updatedAt?: { toMillis(): number };
}
export interface AppSettings {
  eventDate?: { toMillis(): number }; fullHuntUnlocked: boolean;
  countdownEnabled: boolean;
  // Play-area polygon drawn by the admin on the ops map.
  gameArea?: { points: Array<{ lat: number; lng: number }> } | null;
  helpMarkdown?: string;
}

// ---- realtime subscriptions ----
export function watchGuest(uid: string, cb: (g: Guest | null) => void): Unsubscribe {
  return onSnapshot(doc(db, "guests", uid), (s) => cb(s.exists() ? (s.data() as Guest) : null));
}
export function watchSettings(cb: (s: AppSettings | null) => void): Unsubscribe {
  return onSnapshot(doc(db, "settings", "app"), (s) => cb(s.exists() ? (s.data() as AppSettings) : null));
}
export function watchCaches(cb: (c: Cache[]) => void): Unsubscribe {
  return onSnapshot(query(collection(db, "caches"), orderBy("powerTier")), (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Cache, "id">) })));
  });
}
export function watchCacheProgress(uid: string, cb: (m: Record<string, CacheProgress>) => void): Unsubscribe {
  return onSnapshot(collection(db, `guests/${uid}/cacheProgress`), (snap) => {
    const m: Record<string, CacheProgress> = {};
    snap.forEach((d) => (m[d.id] = d.data() as CacheProgress));
    cb(m);
  });
}
export function watchBroadcasts(cb: (b: Broadcast[]) => void): Unsubscribe {
  return onSnapshot(query(collection(db, "broadcasts"), orderBy("createdAt", "desc")), (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Broadcast, "id">) })));
  });
}
// Every agent's live position — the map shows everyone by default.
export function watchPositions(cb: (p: AgentPosition[]) => void): Unsubscribe {
  return onSnapshot(collection(db, "positions"), (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<AgentPosition, "id">) })));
  });
}

// ---- one-shot reads ----
export async function listCases(): Promise<CaseFile[]> {
  const snap = await getDocs(collection(db, "cases"));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CaseFile, "id">) }));
}
export async function listCaches(): Promise<Cache[]> {
  const snap = await getDocs(query(collection(db, "caches"), orderBy("powerTier")));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Cache, "id">) }));
}
export async function progressMap(uid: string, sub: string): Promise<Record<string, Progress>> {
  const snap = await getDocs(collection(db, `guests/${uid}/${sub}`));
  const m: Record<string, Progress> = {};
  snap.forEach((d) => (m[d.id] = d.data() as Progress));
  return m;
}
// ---- callables ----
export const submitCaseAnswer = callable<
  { caseId: string; answer: string },
  { solved: boolean; correct?: boolean; intelAwarded?: number; alreadySolved?: boolean }
>("submitCaseAnswer");

export const buyHint = callable<
  { cacheId: string },
  { bought: boolean; noMoreHints?: boolean; index?: number; hint?: string; remaining?: number; newBalance?: number }
>("buyHint");

export const revealCacheLocation = callable<
  { cacheId: string; answer?: string },
  { revealed: boolean; correct?: boolean; already?: boolean; via?: "riddle" | "intel"; lat?: number; lng?: number; cost?: number; newBalance?: number }
>("revealCacheLocation");

export const submitCacheAnswer = callable<
  { cacheId: string; answer: string; photoPath: string; photoURL?: string },
  { claimed: boolean; correct?: boolean; alreadyClaimed?: boolean; first?: boolean; powerTier?: number; name?: string; locationText?: string | null }
>("submitCacheAnswer");

export const getLeaderboard = callable<
  Record<string, never>,
  { leaderboard: Array<{ guestId: string; codename: string; totalIntel: number; avatarPath: string | null; isMe: boolean; rank: number }> }
>("getLeaderboard");

export const generateAvatar = callable<{ sourcePath: string; targetGuestId?: string }, { ok: boolean; avatarPath: string }>("generateAvatar");
export const sendBroadcast = callable<{ title: string; body: string; type: string; audience: string }, { ok: boolean; pushed: number }>("sendBroadcast");
export const adminResetBinding = callable<{ guestId: string }, { ok: boolean }>("adminResetBinding");
